import { shortLivedCachedAxios, noCacheAxios, invalidateCache } from '../../../shared/config/cachedAxiosConfig';
import { getAuthHeaders } from '../../../shared/services/auth/authHeaders';
import { NOTIFICATION_ENDPOINTS } from '../config/organizationApiEndpoints';
import logger from '../../../shared/services/logging/logger';
import { validateUserId, validateNotificationId } from '../utils/validators';

export const NotificationService = {
  getAllNotifications: async (userId) => {
    validateUserId(userId);

    try {
      // FIXED: Await the async getAuthHeaders function
      const headers = await getAuthHeaders();
      
      const response = await shortLivedCachedAxios.get(NOTIFICATION_ENDPOINTS.getAll(userId), { headers });
      return response.data;
    } catch (error) {
      logger.error('Failed to get notifications', { method: 'getAllNotifications', error: error.message, userId });
      throw error;
    }
  },

  getAllUnreadNotifications: async (userId) => {
    validateUserId(userId);

    try {
      // FIXED: Await the async getAuthHeaders function
      const headers = await getAuthHeaders();
      
      const response = await shortLivedCachedAxios.get(NOTIFICATION_ENDPOINTS.getAllUnread(userId), { headers });
      return response.data;
    } catch (error) {
      logger.error('Failed to get unread notifications', { method: 'getAllUnreadNotifications', error: error.message, userId });
      throw error;
    }
  },

  createNotification: async (userId, notificationData) => {
  validateUserId(userId);

  try {
    logger.info('Creating notification', { method: 'createNotification', userId, notificationData });

    const headers = await getAuthHeaders();

    const response = await noCacheAxios.post(
      NOTIFICATION_ENDPOINTS.create(userId),
      notificationData,
      { headers, _skipAuthRedirect: true }
    );
    invalidateCache(shortLivedCachedAxios);
    return response.data;
  } catch (error) {
    logger.error('Notification creation failed', { method: 'createNotification', error: error.response?.data || error.message, userId });
    throw error;
  }
},

  markAsRead: async (userId, notificationId) => {
    validateUserId(userId);
    validateNotificationId(notificationId);

    try {
      const headers = await getAuthHeaders();
      // Backend only accepts X-Auth-Token on this endpoint; drop Authorization to avoid 500
      const { Authorization: _drop, ...safeHeaders } = headers;

      const response = await noCacheAxios.put(
        NOTIFICATION_ENDPOINTS.markAsRead(userId, notificationId),
        {},
        { headers: safeHeaders }
      );
      invalidateCache(shortLivedCachedAxios);
      return response.data;
    } catch (error) {
      logger.error('Failed to mark notification as read', { method: 'markAsRead', error: error.message, userId, notificationId });
      throw error;
    }
  },

  deleteNotification: async (userId, notificationId) => {
    validateUserId(userId);
    validateNotificationId(notificationId);

    try {
      // FIXED: Await the async getAuthHeaders function
      const headers = await getAuthHeaders();

      await noCacheAxios.delete(
        NOTIFICATION_ENDPOINTS.delete(userId, notificationId),
        { headers }
      );
      invalidateCache(shortLivedCachedAxios);
    } catch (error) {
      logger.error('Failed to delete notification', { method: 'deleteNotification', error: error.message, userId, notificationId });
      throw error;
    }
  },

  // Helper method to format notification data for the UI
  formatNotificationForUI: (notificationData) => {
    return {
      notificationId: notificationData.notificationId || notificationData.id,
      title: notificationData.title,
      description: notificationData.description || notificationData.message,
      date: notificationData.date || notificationData.created_at?.split('T')[0],
      time: notificationData.time || notificationData.created_at?.split('T')[1] || new Date().toISOString().split('T')[1],
      read: notificationData.read || notificationData.is_read || false,
      type: notificationData.type || notificationData.notification_type || 'INFO',
      sourceId: notificationData.sourceId || notificationData.source_id || '',
      actionUrl: notificationData.actionUrl || notificationData.action_url || ''
    };
  },

  // Helper method to format notification data for the API
  formatNotificationForAPI: (notificationData) => {
    return {
      title: notificationData.title,
      description: notificationData.description,
      type: notificationData.type || 'INFO',
      sourceId: notificationData.sourceId || '',
      actionUrl: notificationData.actionUrl || ''
    };
  },

  // Batch operations
  markAllAsRead: async (userId) => {
    validateUserId(userId);

    try {
      const headers = await getAuthHeaders();
      const { Authorization: _drop, ...safeHeaders } = headers;

      const response = await noCacheAxios.put(
        NOTIFICATION_ENDPOINTS.markAllAsRead(userId),
        {},
        { headers: safeHeaders }
      );
      invalidateCache(shortLivedCachedAxios);
      return response.data;
    } catch (error) {
      logger.error('Failed to mark all notifications as read', { method: 'markAllAsRead', error: error.message, userId });
      throw error;
    }
  },

  deleteMultiple: async (userId, notificationIds) => {
    validateUserId(userId);

    try {
      // Since there's no batch endpoint, we'll delete each one individually
      const promises = notificationIds.map(notificationId =>
        NotificationService.deleteNotification(userId, notificationId)
      );
      
      await Promise.all(promises);
    } catch (error) {
      logger.error('Failed to delete multiple notifications', { method: 'deleteMultiple', error: error.message, userId });
      throw error;
    }
  }
};